import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../../utils/axios';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const RoleManagementPanel = ({ onCreateRole, onUpdateRole, onDeleteRole }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDescription, setEditRoleDescription] = useState('');
  const [roles, setRoles] = useState([]);
  const [permissionsData, setPermissionsData] = useState({});
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const notifySuccess = (message) => {
    setError(null);
    setSuccessMessage(message);
    toast.success(message);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([fetchRoles(), fetchPermissions()]);
    } catch (err) {
      console.error('Initial fetch error:', err);
      setError('Failed to load initial data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      const formattedRoles = (response.data || []).map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description || '',
        userCount: role.users_count ?? role.user_count ?? 0,
        permissions: role.permissions_grouped || {},
      }));
      setRoles(formattedRoles);

      // Keep current selection synced or select the first role
      setSelectedRole((prev) => {
        if (!prev) return formattedRoles[0] || null;
        const found = formattedRoles.find((r) => r.id === prev.id);
        return found || formattedRoles[0] || null;
      });
    } catch (error) {
      console.error('Fetch Roles Error:', error);
      throw error;
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axiosInstance.get('/permissions');
      setPermissionsData(response.data || {});
    } catch (error) {
      console.error('Fetch Permissions Error:', error);
      throw error;
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName?.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await axiosInstance.post('/roles', {
        name: newRoleName,
        description: newRoleDescription,
      });

      const newRole = {
        id: response.data.data?.id || response.data.id || Date.now(),
        name: response.data.data?.name || newRoleName,
        description: response.data.data?.description || newRoleDescription,
        userCount: 0,
        permissions: {},
      };

      await fetchRoles();
      setSelectedRole(newRole);
      setNewRoleName('');
      setNewRoleDescription('');
      setIsCreatingRole(false);

      notifySuccess(response.data.message || `Role "${newRole.name}" created successfully`);
      if (onCreateRole) onCreateRole(newRole);
    } catch (error) {
      console.error('Create Role Error:', error);
      const msg = error?.response?.data?.message || 'Failed to create role. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRoleInfo = async () => {
    if (!selectedRole) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await axiosInstance.put(`/roles/${selectedRole.id}`, {
        name: editRoleName,
        description: editRoleDescription,
      });

      await fetchRoles();
      setIsEditingRole(false);

      notifySuccess(response.data.message || `Role "${editRoleName}" updated successfully`);
      if (onUpdateRole)
        onUpdateRole({ ...selectedRole, name: editRoleName, description: editRoleDescription });
    } catch (error) {
      console.error('Update Role Error:', error);
      const msg = error?.response?.data?.message || 'Failed to update role. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle single permission for a role
  const handlePermissionChange = async (roleId, module, permission, checked) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    const updatedPermissions = { ...role.permissions };
    const currentModulePerms = Array.isArray(updatedPermissions[module])
      ? [...updatedPermissions[module]]
      : [];

    if (checked) {
      if (!currentModulePerms.includes(permission)) {
        currentModulePerms.push(permission);
      }
    } else {
      const idx = currentModulePerms.indexOf(permission);
      if (idx > -1) {
        currentModulePerms.splice(idx, 1);
      }
    }

    updatedPermissions[module] = currentModulePerms;

    // Immediately update local state for responsive, zero-lag UI
    const updatedRole = { ...role, permissions: updatedPermissions };
    setRoles(roles.map((r) => (r.id === roleId ? updatedRole : r)));

    if (selectedRole?.id === roleId) {
      setSelectedRole(updatedRole);
    }

    try {
      const permissionIds = [];
      Object.keys(updatedPermissions).forEach((moduleName) => {
        if (Array.isArray(updatedPermissions[moduleName])) {
          updatedPermissions[moduleName].forEach((perm) => {
            const foundPermission = permissionsData[moduleName]?.find(
              (item) => item.permission === perm
            );
            if (foundPermission) {
              permissionIds.push(foundPermission.id);
            }
          });
        }
      });

      const response = await axiosInstance.put(`/roles/${roleId}/permissions`, {
        permission_ids: permissionIds,
      });
      notifySuccess(response.data?.message || 'Permission updated successfully');
    } catch (error) {
      console.error('Permission Update Error:', error);
      const msg = error?.response?.data?.message || 'Failed to update permissions.';
      setError(msg);
      toast.error(msg);
      // Revert on error
      await fetchRoles();
    }
  };

  // Select all permissions in a module
  const handleSelectAllPermissions = async (module) => {
    if (!selectedRole) return;

    const modulePermissions = permissionsData[module]?.map((p) => p.permission) || [];
    const currentPermissions = Array.isArray(selectedRole.permissions?.[module])
      ? selectedRole.permissions[module]
      : [];

    const allSelected =
      modulePermissions.length > 0 &&
      modulePermissions.every((perm) => currentPermissions.includes(perm));

    const updatedPermissions = { ...selectedRole.permissions };

    if (allSelected) {
      updatedPermissions[module] = [];
    } else {
      updatedPermissions[module] = [...modulePermissions];
    }

    const updatedRole = { ...selectedRole, permissions: updatedPermissions };
    setRoles(roles.map((r) => (r.id === selectedRole.id ? updatedRole : r)));
    setSelectedRole(updatedRole);

    const permissionIds = [];
    Object.keys(updatedPermissions).forEach((moduleName) => {
      if (Array.isArray(updatedPermissions[moduleName])) {
        updatedPermissions[moduleName].forEach((perm) => {
          const foundPermission = permissionsData[moduleName]?.find(
            (item) => item.permission === perm
          );
          if (foundPermission) {
            permissionIds.push(foundPermission.id);
          }
        });
      }
    });

    try {
      const res = await axiosInstance.put(`/roles/${selectedRole.id}/permissions`, {
        permission_ids: permissionIds,
      });
      notifySuccess(res.data?.message || `Permissions for ${module} updated`);
    } catch (error) {
      console.error('Permission Update Error:', error);
      toast.error('Failed to update permissions');
      await fetchRoles();
    }
  };

  const isAllPermissionsSelected = (module) => {
    if (!selectedRole) return false;
    const modulePermissions = permissionsData[module]?.map((p) => p.permission) || [];
    const currentPermissions = Array.isArray(selectedRole.permissions?.[module])
      ? selectedRole.permissions[module]
      : [];
    return (
      modulePermissions.length > 0 &&
      modulePermissions.every((perm) => currentPermissions.includes(perm))
    );
  };

  const handleDeleteRole = async (roleId) => {
    const roleToDelete = roles.find((r) => r.id === roleId);

    if (roleToDelete?.userCount > 0) {
      const msg = `Cannot delete "${roleToDelete.name}" because it has ${roleToDelete.userCount} assigned users.`;
      toast.error(msg);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the role "${roleToDelete?.name}"?`)) return;

    try {
      const response = await axiosInstance.delete(`/roles/${roleId}`);
      notifySuccess(response.data?.message || `Role "${roleToDelete?.name}" deleted successfully`);
      await fetchRoles();
      if (onDeleteRole) onDeleteRole(roleId);
    } catch (error) {
      console.error('Delete Role Error:', error);
      const msg = error?.response?.data?.message || 'Failed to delete role.';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleCloneRole = async () => {
    if (!selectedRole) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await axiosInstance.post('/roles', {
        name: `${selectedRole.name} Copy`,
        description: selectedRole.description,
      });

      const newRoleId = response.data.data?.id || response.data.id;
      const permissionIds = [];

      Object.keys(selectedRole.permissions || {}).forEach((moduleName) => {
        if (Array.isArray(selectedRole.permissions[moduleName])) {
          selectedRole.permissions[moduleName].forEach((perm) => {
            const foundPermission = permissionsData[moduleName]?.find(
              (item) => item.permission === perm
            );
            if (foundPermission) {
              permissionIds.push(foundPermission.id);
            }
          });
        }
      });

      await axiosInstance.put(`/roles/${newRoleId}/permissions`, {
        permission_ids: permissionIds,
      });

      await fetchRoles();
      notifySuccess(`Role "${selectedRole.name} Copy" cloned successfully`);
    } catch (error) {
      console.error('Clone Role Error:', error);
      const msg = error?.response?.data?.message || 'Failed to clone role.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditingRole = () => {
    setEditRoleName(selectedRole.name);
    setEditRoleDescription(selectedRole.description || '');
    setIsEditingRole(true);
  };

  const handleCreateDrawerClose = () => {
    setIsCreatingRole(false);
    setNewRoleName('');
    setNewRoleDescription('');
  };

  const handleEditDrawerClose = () => {
    setIsEditingRole(false);
    setEditRoleName('');
    setEditRoleDescription('');
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Reset system permissions and standard roles to defaults?')) return;
    try {
      setIsSubmitting(true);
      const res = await axiosInstance.post('/permissions/seed-defaults');
      notifySuccess(res.data?.message || 'Default system roles & permissions refreshed successfully');
      await fetchInitialData();
    } catch (err) {
      console.error('Seed error:', err);
      toast.error('Failed to seed default permissions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'create':
        return 'Plus';
      case 'read':
        return 'Eye';
      case 'update':
        return 'Edit3';
      case 'delete':
        return 'Trash2';
      case 'export':
        return 'Download';
      case 'import':
        return 'Upload';
      case 'assign':
        return 'UserCheck';
      case 'transfer':
        return 'ArrowRightLeft';
      case 'audit':
        return 'ClipboardCheck';
      case 'retire':
        return 'Archive';
      case 'clearance':
        return 'ShieldCheck';
      case 'suspend':
        return 'Ban';
      case 'assign_role':
        return 'ShieldAlert';
      case 'assign_permissions':
        return 'Key';
      case 'renew':
        return 'RefreshCw';
      case 'approve':
        return 'CheckCircle2';
      case 'resolve':
        return 'CheckCheck';
      case 'verify':
        return 'CheckCheck';
      case 'claim':
        return 'FileCheck';
      case 'terminate_session':
        return 'LogOut';
      default:
        return 'Check';
    }
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case 'create':
        return 'text-emerald-500';
      case 'read':
        return 'text-blue-500';
      case 'update':
        return 'text-amber-500';
      case 'delete':
        return 'text-rose-500';
      case 'export':
      case 'import':
        return 'text-cyan-500';
      case 'assign':
      case 'transfer':
        return 'text-purple-500';
      case 'audit':
      case 'clearance':
      case 'verify':
      case 'approve':
      case 'resolve':
        return 'text-emerald-600';
      case 'renew':
        return 'text-teal-500';
      case 'suspend':
      case 'terminate_session':
        return 'text-rose-600';
      default:
        return 'text-primary';
    }
  };

  // Filter roles by search
  const filteredRoles = useMemo(() => {
    if (!roleSearchTerm.trim()) return roles;
    const term = roleSearchTerm.toLowerCase();
    return roles.filter(
      (r) =>
        r.name?.toLowerCase().includes(term) ||
        r.description?.toLowerCase().includes(term)
    );
  }, [roles, roleSearchTerm]);

  // Filter permission modules by search
  const filteredModules = useMemo(() => {
    const modules = Object.keys(permissionsData || {});
    if (!permissionSearchTerm.trim()) return modules;

    const term = permissionSearchTerm.toLowerCase();
    return modules.filter((mod) => {
      if (mod.toLowerCase().includes(term)) return true;
      const perms = permissionsData[mod] || [];
      return perms.some((p) => p.permission?.toLowerCase().includes(term));
    });
  }, [permissionsData, permissionSearchTerm]);

  // Count active permissions for the selected role
  const totalAssignedPermissionsCount = useMemo(() => {
    if (!selectedRole?.permissions) return 0;
    let count = 0;
    Object.keys(selectedRole.permissions).forEach((mod) => {
      if (Array.isArray(selectedRole.permissions[mod])) {
        count += selectedRole.permissions[mod].length;
      }
    });
    return count;
  }, [selectedRole]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
          <p className="mt-3 text-sm text-muted-foreground">Loading role security matrix...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center max-w-md mx-auto my-12">
        <Icon name="AlertCircle" size={42} className="text-error mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-card-foreground mb-1">Error Loading Roles</h3>
        <p className="text-xs text-muted-foreground mb-4">{error}</p>
        <Button variant="default" size="sm" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-xl shadow-subtle overflow-hidden">
        {/* Banner Alert */}
        {successMessage && (
          <div className="mx-6 mt-6 p-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between text-emerald-700 dark:text-emerald-400">
            <div className="flex items-center space-x-2.5">
              <Icon name="CheckCircle2" size={17} className="text-emerald-500 shrink-0" />
              <span className="text-xs font-semibold">{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-emerald-600 hover:text-emerald-800 dark:hover:text-emerald-300 ml-3"
              type="button"
            >
              <Icon name="X" size={15} />
            </button>
          </div>
        )}

        {/* Panel Header */}
        <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-4 bg-muted/10">
          <div>
            <h3 className="text-lg font-bold text-card-foreground">Access Control & Role Matrix</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Define enterprise roles and configure granular read, write, and administrative rights
            </p>
          </div>

          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSeedDefaults}
              iconName="RefreshCw"
              iconPosition="left"
              title="Reset system roles and modules to standard defaults"
              className="h-8 text-xs font-medium"
            >
              Reset System Defaults
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsCreatingRole(true)}
              iconName="Plus"
              iconPosition="left"
              className="h-8 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Create New Role
            </Button>
          </div>
        </div>

        {/* Main Grid: Left Roles List / Right Permission Matrix */}
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-border">
          {/* ========================================================================= */}
          {/* LEFT COLUMN: ROLES LIST */}
          {/* ========================================================================= */}
          <div className="p-5 flex flex-col h-full bg-card">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Configured Roles ({roles.length})
              </span>
            </div>

            {/* Role Search */}
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Search roles..."
                value={roleSearchTerm}
                onChange={(e) => setRoleSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 pl-8 text-xs rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <Icon name="Search" size={13} className="absolute left-2.5 top-2 text-muted-foreground" />
            </div>

            {/* Roles List */}
            <div className="space-y-2 max-h-[580px] overflow-y-auto pr-1 scrollbar-thin">
              {filteredRoles.length > 0 ? (
                filteredRoles.map((role) => {
                  const isSelected = selectedRole?.id === role.id;
                  const moduleCount = Object.keys(role.permissions || {}).filter(
                    (k) => Array.isArray(role.permissions[k]) && role.permissions[k].length > 0
                  ).length;

                  return (
                    <div
                      key={role.id}
                      onClick={() => {
                        setSelectedRole(role);
                        setIsEditingRole(false);
                      }}
                      className={`p-3 rounded-xl border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow-xs ring-1 ring-primary/30'
                          : 'border-border hover:border-primary/40 hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <div
                            className={`w-6 h-6 rounded-md flex items-center justify-center text-xs ${
                              isSelected
                                ? 'bg-primary text-white font-bold'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            <Icon name="Shield" size={13} />
                          </div>
                          <h5 className="font-semibold text-xs text-card-foreground">{role.name}</h5>
                        </div>
                        <span className="text-[11px] font-mono font-medium px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">
                          {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
                        </span>
                      </div>

                      <p className="text-[11px] text-muted-foreground line-clamp-2 pl-8">
                        {role.description || 'No description provided'}
                      </p>

                      <div className="mt-2 pl-8 flex items-center space-x-2 text-[10px] text-muted-foreground">
                        <span className="flex items-center">
                          <Icon name="Key" size={10} className="mr-1 text-primary" />
                          {moduleCount} active module(s)
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-10">
                  <Icon name="ShieldAlert" size={28} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No matching roles found</p>
                </div>
              )}
            </div>
          </div>

          {/* ========================================================================= */}
          {/* RIGHT COLUMN: PERMISSION MATRIX */}
          {/* ========================================================================= */}
          <div className="lg:col-span-2 p-6 bg-card flex flex-col">
            {selectedRole ? (
              <div className="space-y-5">
                {/* Role Details Bar */}
                <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-border">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center space-x-2.5">
                      <h4 className="font-bold text-card-foreground text-base">{selectedRole.name}</h4>
                      <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-0.5 rounded-full">
                        {selectedRole.userCount} {selectedRole.userCount === 1 ? 'user' : 'users'}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground bg-muted/40 px-2 py-0.5 rounded">
                        {totalAssignedPermissionsCount} permissions active
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedRole.description || 'Custom role permissions configured below.'}
                    </p>
                  </div>

                  {/* Actions for the selected Role */}
                  <div className="flex items-center space-x-2 flex-wrap gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit"
                      iconPosition="left"
                      onClick={startEditingRole}
                      className="h-8 text-xs font-medium"
                    >
                      Edit Role
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Copy"
                      iconPosition="left"
                      onClick={handleCloneRole}
                      className="h-8 text-xs font-medium"
                    >
                      Clone
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteRole(selectedRole.id)}
                      iconName="Trash2"
                      iconPosition="left"
                      disabled={selectedRole.userCount > 0}
                      title={
                        selectedRole.userCount > 0
                          ? `Cannot delete role with ${selectedRole.userCount} assigned users`
                          : 'Delete this role'
                      }
                      className="h-8 text-xs font-medium"
                    >
                      Delete Role
                    </Button>
                  </div>
                </div>

                {/* Search Bar for Permissions */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search permissions or modules (e.g. assets, create, delete, export)..."
                    value={permissionSearchTerm}
                    onChange={(e) => setPermissionSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 pl-9 text-xs rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <Icon name="Search" size={14} className="absolute left-3 top-2.5 text-muted-foreground" />
                  {permissionSearchTerm && (
                    <button
                      onClick={() => setPermissionSearchTerm('')}
                      className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground text-xs"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  )}
                </div>

                {/* Modules & Permissions List */}
                {filteredModules.length > 0 ? (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin">
                    {filteredModules.map((module) => {
                      const allSelected = isAllPermissionsSelected(module);
                      const modulePermissions = permissionsData[module] || [];

                      return (
                        <div
                          key={module}
                          className="border border-border rounded-xl p-4 bg-muted/5 hover:border-primary/30 transition-all"
                        >
                          {/* Module Header */}
                          <div className="flex items-center justify-between mb-3 pb-2 border-b border-border/60">
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-xs uppercase tracking-wider text-card-foreground">
                                {module.replace(/_/g, ' ')}
                              </span>
                              <span className="text-[11px] text-muted-foreground font-mono">
                                ({modulePermissions.length} available)
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleSelectAllPermissions(module)}
                              className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center space-x-1"
                            >
                              <Icon
                                name={allSelected ? 'CheckSquare' : 'Square'}
                                size={13}
                                className="mr-1"
                              />
                              <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
                            </button>
                          </div>

                          {/* Permissions Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                            {modulePermissions.map((permissionData) => {
                              const permission = permissionData.permission;
                              const isChecked = Boolean(
                                Array.isArray(selectedRole?.permissions?.[module]) &&
                                  selectedRole.permissions[module].includes(permission)
                              );

                              return (
                                <div
                                  key={permission}
                                  onClick={() =>
                                    handlePermissionChange(
                                      selectedRole.id,
                                      module,
                                      permission,
                                      !isChecked
                                    )
                                  }
                                  className={`flex items-center space-x-2.5 px-3 py-2 rounded-lg border transition-all cursor-pointer select-none ${
                                    isChecked
                                      ? 'border-primary/50 bg-primary/10 text-primary shadow-xs font-medium'
                                      : 'border-border bg-card hover:bg-muted/40 text-muted-foreground'
                                  }`}
                                >
                                  {/* Checkbox */}
                                  <div className="flex items-center justify-center pointer-events-none">
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(newChecked) =>
                                        handlePermissionChange(
                                          selectedRole.id,
                                          module,
                                          permission,
                                          newChecked
                                        )
                                      }
                                    />
                                  </div>

                                  {/* Action Icon */}
                                  <Icon
                                    name={getPermissionIcon(permission)}
                                    size={14}
                                    className={getPermissionColor(permission)}
                                  />

                                  {/* Clean permission name without extra buttons! */}
                                  <span className="text-xs capitalize truncate font-medium">
                                    {permission.replace(/_/g, ' ')}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 border border-dashed border-border rounded-xl">
                    <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">
                      No permissions match "{permissionSearchTerm}"
                    </p>
                  </div>
                )}

                <div className="p-3 bg-muted/20 border border-border rounded-xl flex items-center space-x-2.5 text-xs text-muted-foreground">
                  <Icon name="Info" size={15} className="text-primary shrink-0" />
                  <span>
                    Permission assignments sync live with MySQL. Changes immediately govern all API and menu access for assigned users.
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Icon name="Shield" size={42} className="text-muted-foreground mb-3" />
                <h4 className="text-base font-semibold text-card-foreground mb-1">Select a Role</h4>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Choose an enterprise role from the left list to review or modify its active permission matrix.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* CREATE ROLE DRAWER */}
      {/* ========================================================================= */}
      {isCreatingRole && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40 transition-opacity"
            onClick={handleCreateDrawerClose}
          />

          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-card shadow-2xl z-50 transform transition-transform duration-300 ease-in-out animate-slide-in-right">
            <div className="flex items-center justify-between p-5 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Plus" size={18} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-card-foreground">Create New Role</h3>
                  <p className="text-xs text-muted-foreground">Define role identity and description</p>
                </div>
              </div>
              <button
                onClick={handleCreateDrawerClose}
                className="text-muted-foreground hover:text-card-foreground p-1.5 hover:bg-muted rounded-lg"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="overflow-y-auto h-[calc(100%-130px)] p-6 space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-card-foreground mb-1.5">
                  Role Name <span className="text-error">*</span>
                </label>
                <Input
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Asset Auditor, Warehouse Supervisor"
                  autoFocus
                  className="w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-card-foreground mb-1.5">
                  Role Description
                </label>
                <textarea
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  placeholder="Describe operational responsibilities and scope of custody..."
                  rows={4}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-card-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>

              <div className="p-3.5 bg-muted/20 rounded-xl border border-border flex items-start space-x-2.5">
                <Icon name="Lock" size={15} className="text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  New roles initialize with zero permissions. After creation, easily toggle capabilities on the permission matrix.
                </p>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-muted/10 flex items-center justify-end space-x-2">
              <Button variant="ghost" size="sm" onClick={handleCreateDrawerClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateRole}
                disabled={isSubmitting || !newRoleName?.trim()}
                iconName={isSubmitting ? 'Loader2' : 'Plus'}
                iconPosition="left"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs"
              >
                {isSubmitting ? 'Creating...' : 'Create Role'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ========================================================================= */}
      {/* EDIT ROLE DRAWER */}
      {/* ========================================================================= */}
      {isEditingRole && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40 transition-opacity"
            onClick={handleEditDrawerClose}
          />

          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-card shadow-2xl z-50 transform transition-transform duration-300 ease-in-out animate-slide-in-right">
            <div className="flex items-center justify-between p-5 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Edit" size={18} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-card-foreground">Edit Role Details</h3>
                  <p className="text-xs text-muted-foreground">Update title and description</p>
                </div>
              </div>
              <button
                onClick={handleEditDrawerClose}
                className="text-muted-foreground hover:text-card-foreground p-1.5 hover:bg-muted rounded-lg"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="overflow-y-auto h-[calc(100%-130px)] p-6 space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-card-foreground mb-1.5">
                  Role Name <span className="text-error">*</span>
                </label>
                <Input
                  value={editRoleName}
                  onChange={(e) => setEditRoleName(e.target.value)}
                  placeholder="Enter role name"
                  className="w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-card-foreground mb-1.5">
                  Role Description
                </label>
                <textarea
                  value={editRoleDescription}
                  onChange={(e) => setEditRoleDescription(e.target.value)}
                  placeholder="Describe role responsibilities..."
                  rows={4}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-card-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>

              <div className="p-3.5 bg-muted/20 rounded-xl border border-border flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <Icon name="Users" size={15} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    Currently assigned to <strong>{selectedRole?.userCount}</strong> user(s)
                  </span>
                </div>
                <Icon name="UserCheck" size={15} className="text-emerald-500" />
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-muted/10 flex items-center justify-end space-x-2">
              <Button variant="ghost" size="sm" onClick={handleEditDrawerClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleUpdateRoleInfo}
                disabled={isSubmitting || !editRoleName?.trim()}
                iconName={isSubmitting ? 'Loader2' : 'Save'}
                iconPosition="left"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.25s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .scrollbar-thin::-webkit-scrollbar {
          width: 5px;
        }

        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }

        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(150, 150, 150, 0.2);
          border-radius: 4px;
        }

        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(150, 150, 150, 0.35);
        }
      `}</style>
    </>
  );
};

export default RoleManagementPanel;
